import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, SlidersHorizontal } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import DestinationCard from "@/components/DestinationCard";
import { DESTINATIONS, CATEGORIES } from "@/data/destinations";

const NEAR_CITIES = [
  { id: "dehradun", label: "Near Dehradun" },
  { id: "haridwar", label: "Near Haridwar" },
  { id: "rishikesh", label: "Near Rishikesh" },
];

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");
  const [near, setNear] = useState([]);

  const setCategory = (id) => {
    const next = new URLSearchParams(params);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setParams(next, { replace: true });
  };

  const toggleNear = (id) => setNear((n) => (n.includes(id) ? n.filter((x) => x !== id) : [...n, id]));

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    return DESTINATIONS.filter((d) => {
      if (category !== "all" && !(d.categories || []).includes(category)) return false;
      if (region !== "all" && d.region !== region) return false;
      if (near.length && !near.every((n) => (d.near || []).includes(n))) return false;
      if (query && !(`${d.name} ${d.base} ${d.region} ${d.description}`.toLowerCase().includes(query))) return false;
      return true;
    });
  }, [q, category, region, near]);

  return (
    <>
      <Seo
        title="Explore Uttarakhand — 100+ Destinations, Photos & Taxi Trips | Tingadya Travels"
        description="Browse Uttarakhand's temples, hill stations, lakes, waterfalls, national parks, treks and heritage towns with photos, access notes and taxi enquiries by Tingadya Travels."
      />
      <section className="relative overflow-hidden pb-10 pt-28 sm:pt-36" data-testid="explore-hero">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(212,175,55,0.14),transparent)]" />
        <div className="container-x relative z-10">
          <p className="eyebrow">The Uttarakhand Explorer</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl">Explore Uttarakhand</h1>
          <p className="mt-4 max-w-2xl leading-relaxed text-mist">
            {DESTINATIONS.length} destinations across Garhwal and Kumaon — filter by mood, search by name, and check how you reach each place before you go.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <div className="sticky top-16 z-30 border-y border-white/10 bg-ink/85 backdrop-blur-xl sm:top-[72px]" data-testid="explorer-filter-bar">
        <div className="container-x flex flex-col gap-2.5 py-3 lg:flex-row lg:items-center lg:gap-3 lg:py-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slategray" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a destination — Kedarnath, Auli, Nainital…"
              data-testid="explorer-search-input"
              aria-label="Search destinations"
              className="w-full rounded-full border border-white/15 bg-white/5 py-2.5 pl-11 pr-4 text-sm text-snow placeholder:text-slategray outline-none transition-colors focus:border-gold/60"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:pb-0">
            <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="explorer-region-select" aria-label="Filter by region" className="shrink-0 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-snow outline-none focus:border-gold/60">
              <option value="all">All regions</option>
              <option value="Garhwal">Garhwal</option>
              <option value="Kumaon">Kumaon</option>
            </select>
            {NEAR_CITIES.map((c) => (
              <button
                key={c.id}
                onClick={() => toggleNear(c.id)}
                data-testid={`near-chip-${c.id}`}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${near.includes(c.id) ? "border-gold bg-gold text-ink" : "border-white/15 bg-white/5 text-mist hover:border-gold/50"}`}
              >
                <MapPin size={12} /> {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="container-x flex gap-2 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="explorer-category-chips">
          <button
            onClick={() => setCategory("all")}
            data-testid="category-chip-all"
            className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${category === "all" ? "border-gold bg-gold text-ink" : "border-white/15 bg-white/5 text-mist hover:border-gold/50"}`}
          >
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              data-testid={`category-chip-${c.id}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${category === c.id ? "border-gold bg-gold text-ink" : "border-white/15 bg-white/5 text-mist hover:border-gold/50"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <section className="section-pad" data-testid="explorer-results-section">
        <div className="container-x">
          <p className="mb-8 flex items-center gap-2 text-sm text-slategray" data-testid="explorer-results-count">
            <SlidersHorizontal size={14} className="text-gold" /> {results.length} {results.length === 1 ? "destination" : "destinations"}
          </p>
          {results.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-coal p-14 text-center" data-testid="explorer-empty-state">
              <Search size={30} className="mx-auto text-gold" />
              <h2 className="mt-4 font-display text-2xl text-white">No destinations match that search</h2>
              <p className="mt-2 text-sm text-mist">Try a shorter name, or clear the filters to see everything.</p>
              <button onClick={() => { setQ(""); setRegion("all"); setNear([]); setCategory("all"); }} data-testid="explorer-clear-filters-button" className="btn-gold mt-6">
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {results.map((d) => (
                <DestinationCard key={d.slug} d={d} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
